/**
 * The minimum font size for subtitles.
 */
export const MIN_SUBTITLES_FONT_SIZE = 16;

/**
 * The substring that identifies a source name as a synthetic per-language translated-voice source (see
 * {@code TranslationOrchestrator.syntheticSourceName} on the Jicofo side: {@code "<speakerId>-rtat-<language>"}).
 * Used to tell a speaker's original audio source apart from their (possibly several) synthetic ones once both
 * exist as separate remote tracks for the same participant.
 */
export const SYNTHETIC_TRANSLATED_SOURCE_MARKER = '-rtat-';

/**
 * How many seconds of microphone audio a voice-clone enrollment records. Inworld recommends a 10-30s clip.
 * Shared so the recorder and the countdown shown on the button can never disagree.
 */
export const CLONE_RECORDING_SECONDS = 10;

/**
 * Default Inworld API host, overridable with `config.transcription.inworld.baseUrl`.
 */
export const DEFAULT_INWORLD_BASE_URL = 'https://api.inworld.ai';

/**
 * Inworld voice cloning endpoint, appended to the base URL.
 */
export const INWORLD_CLONE_PATH = '/voices/v1/voices:clone';

/**
 * Local storage key holding the Inworld voice id created for this browser.
 *
 * Kept client-side because the clone is created by the browser against Inworld directly; the id is all the
 * server needs, and keeping it here means the picker still works when the user rejoins.
 */
export const VOICE_CLONE_ID_KEY = 'meethour.voiceCloneId';

/**
 * Local storage key holding whether the user picked their cloned voice over the default one.
 */
export const VOICE_CLONE_SELECTED_KEY = 'meethour.voiceCloneSelected';

/**
 * The languages translated voice can actually be synthesized into, as two-letter codes.
 *
 * Translation (captions) covers far more languages than text-to-speech does, so a listener can pick a caption
 * language for which no voice exists - the pipeline then transcribes, translates and asks for synthesis that
 * cannot be produced. The toggle is disabled for those languages instead, with the reason shown.
 *
 * Matched two letters at a time so regional tags line up: the provider lists its own defaults as en-US, es-ES,
 * hi-IN and so on.
 *
 * This is the intersection of two things, not Inworld's catalogue on its own. A language is only offerable when
 * LibreTranslate can translate *into* it (see libre-supported-langs.json) AND Inworld can speak it - offering
 * one without the other produces a toggle that cannot work. Two of LibreTranslate's 46 targets are dropped for
 * that reason: `eo` (Inworld has none) and `ga` (Inworld Tier 3 only). Codes are LibreTranslate's: Jigasi maps
 * `zt` to Inworld's `zh-TW` and `tl` to `fil` (see LanguageCodes there).
 *
 * Inworld grades its own coverage, and the two grades here are not equal:
 *   Tier 1, evaluated, highest quality - en es fr de it pt pl nl ru ar ja ko zh hi he
 *   Tier 2, supported but quality varies by language and voice - everything else below
 * Tier 3 (best-effort, unevaluated, no text normalization) is deliberately excluded.
 *
 * Override per deployment with `config.transcription.ttsLanguages` when the provider's coverage changes, so this
 * does not need a code change - and to fall back to Tier 1 alone if Tier 2 quality disappoints in practice.
 */
export const DEFAULT_TTS_LANGUAGES = [

    // Tier 1
    'ar', 'de', 'en', 'es', 'fr', 'he', 'hi', 'it', 'ja', 'ko', 'nl', 'pl', 'pt', 'ru', 'zh',

    // Tier 2
    'az', 'bg', 'bn', 'ca', 'cs', 'da', 'el', 'et', 'eu', 'fa', 'fi', 'gl', 'hu', 'id', 'lt', 'lv',
    'ms', 'nb', 'ro', 'sk', 'sl', 'sq', 'sv', 'th', 'tl', 'tr', 'uk', 'ur', 'zt'
];
